import { memo, SyntheticEvent } from "react";

import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";

import Close from "@zenith/assets/icons/Close";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { GreenTick, WarningTriangle } from "@zenith/assets/icons/components";
import { useNotificationContext } from "@zenith/context";
import CheckCircle from "@zenith/assets/icons/CheckCircle";

/**
 * Props for Toast Snackbar Component.
 * @interface
 * @name ToastSnackbarComponentProps
 * @kind interface
 */
interface ToastSnackbarComponentProps extends SnackbarOrigin {
  /**
   * Status of the Toast Snackbar message
   * (`"success"`, `"warning"` or `"error"`).
   * @property
   * @name status
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {"success" | "warning" | "error"}
   */
  status: "success" | "warning" | "error";
  /**
   * Text of the Toast Snackbar message.
   * @property
   * @name text
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {string}
   */
  text: string;
  /**
   * Title of the Toast Snackbar message.
   * @property
   * @name title
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {string}
   */
  title: string;
  /**
   * Size of the Toast Snackbar message.
   * If the regular sizes are not enough for what is needed,
   * `"custom"` can be used along with the `sizeClassName` prop.
   * @property
   * @name size
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {"sm" | "md" | "lg" | "custom"}
   */
  size: "sm" | "md" | "lg" | "custom";
  /**
   * The number of milliseconds to wait before automatically calling the
   * `onClose` function. `onClose` should then set the state of the `open`
   * prop to hide the Snackbar. This behavior is disabled by default with
   * the `null` value. Used along with `hasAutoHideDuration`.
   * @default null or 6000
   * @property
   * @name autoHideDuration
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {number | undefined}
   */
  autoHideDuration?: number;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {string | undefined}
   */
  className?: string | undefined;
  /**
   * (optional)
   * Standard HTML Attribute, for Toast Snackbar size classes.
   * Used along with `size` prop.
   * @property
   * @name sizeClassName
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {string | undefined}
   */
  sizeClassName?: string;
  /**
   * (optional)
   * If `true`, enables the `autoHideDuration` value.
   * @property
   * @name hasAutoHideDuration
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {boolean | undefined}
   */
  hasAutoHideDuration?: boolean;
  /**
   * (optional)
   * If `true`, hides the close button.
   * @property
   * @name hideCloseButton
   * @kind property
   * @memberof ToastSnackbarComponentProps
   * @type {boolean | undefined}
   */
  hideCloseButton?: boolean;
}

const AUTO_HIDE_DURATION = 6000;
const CLICKAWAY_REASON = "clickaway";

/**
 * Memoized Toast Snackbar Component.
 * @constant
 * @name ToastSnackbarComponent
 * @kind variable
 * @type {React.NamedExoticComponent<ToastSnackbarComponentProps>}
 */
const ToastSnackbarComponent: React.NamedExoticComponent<ToastSnackbarComponentProps> =
  memo(
    /**
     * Toast Snackbar Component.
     * @function
     * @name ToastSnackbarComponent
     * @kind function
     * @memberof ToastSnackbarComponent
     * @param {ToastSnackbarComponentProps} props Props for Toast Snackbar Component.
     * @returns {JSX.Element} Toast Snackbar Component.
     */
    function ToastSnackbarComponent(
      props: ToastSnackbarComponentProps
    ): JSX.Element {
      const {
        notificationContext: { open },
        setOpen,
      } = useNotificationContext();

      const {
        horizontal,
        status,
        text,
        title,
        vertical,
        size,
        autoHideDuration,
        className,
        hasAutoHideDuration,
      } = props;

      const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
        if (reason === CLICKAWAY_REASON) {
          return;
        }

        setOpen(false);
      };

      function getSize(): string {
        switch (size) {
          case "sm":
            return "min-h-24 w-[322px]";
          case "md":
          case "lg":
            return "min-h-16 w-[668px]";
          case "custom":
            return props.sizeClassName || "";
          default:
            throw new Error("Width not mapped.");
        }
      }

      const sizeClassNames = getSize();

      return (
        <Snackbar
          className={className}
          anchorOrigin={{ vertical, horizontal }}
          open={open}
          onClose={handleClose}
          key={vertical + horizontal}
          disableWindowBlurListener
          autoHideDuration={
            hasAutoHideDuration ? autoHideDuration || AUTO_HIDE_DURATION : null
          }
        >
          <div
            className={`flex flex-row gap-2 rounded-lg ${
              status === "success"
                ? "bg-emerald-50"
                : status === "warning"
                ? "bg-accent-pending-bg"
                : "bg-primary-50"
            } p-4 ${sizeClassNames}`}
          >
            <div className="flex flex-col">
              {status === "success" ? (
                <CheckCircle width={16} height={16} />
              ) : status === "warning" ? (
                <WarningTriangle width={24} height={24} />
              ) : (
                <CloseCircle width={24} height={24} />
              )}
            </div>
            <div className="flex w-11/12 flex-col items-start gap-1">
              <div className="text-sm font-extrabold leading-snug text-zinc-900">
                {title}
              </div>
              <div className="text-sm font-normal leading-none text-zinc-900">
                {text}
              </div>
            </div>
            <div className="-m-2 flex cursor-pointer flex-col items-end justify-start">
              {props.hideCloseButton ? (
                ""
              ) : (
                <IconButton className="cursor-pointer" onClick={handleClose}>
                  <Close />
                </IconButton>
              )}
            </div>
          </div>
        </Snackbar>
      );
    }
  );

export default ToastSnackbarComponent;
